import { useRef } from "react";
import {
  Box,
  Card,
  Typography,
  Chip,
  Checkbox,
  IconButton,
} from "@mui/material";
import { Visibility } from "@mui/icons-material";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { CohortParticipant } from "./types";

const PREVIEW_ROW_ESTIMATE_PX = 96;

const ParticipantCard = ({
  participant,
  selectedParticipants,
  onParticipantClick,
  handleCheckboxToggle,
}: {
  participant: CohortParticipant;
  selectedParticipants: Set<string>;
  onParticipantClick: (participantId: string) => void;
  handleCheckboxToggle: (
    participantId: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
}) => {
  const handleCardClick = () => {
    const syntheticEvent = {
      stopPropagation: () => {},
      target: { checked: !selectedParticipants.has(participant.id) },
    } as React.ChangeEvent<HTMLInputElement>;
    handleCheckboxToggle(participant.id, syntheticEvent);
  };

  const handleViewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onParticipantClick(participant.id);
  };

  return (
    <Card
      key={participant.id}
      sx={{
        p: 1.5,
        cursor: "pointer",
        borderLeft: 3,
        borderLeftColor: "primary.main",
        transition: "box-shadow 0.2s ease-in-out",
        borderRadius: "12px",
        boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
        bgcolor: "background.paper",
        "&:hover": {
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
        },
      }}
      onClick={handleCardClick}
      data-testid={`participant-card-${participant.id}`}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Checkbox
              checked={selectedParticipants.has(participant.id)}
              onChange={(e) => handleCheckboxToggle(participant.id, e)}
              onClick={(e) => e.stopPropagation()}
              sx={{ p: 0 }}
              data-testid={`checkbox-${participant.id}`}
            />
            <Typography
              variant="body1"
              fontWeight={"medium"}
              sx={{ color: "text.primary" }}
            >
              {participant.firstName || participant.lastName
                ? `${participant.firstName ? participant.firstName : ""} ${
                    participant.lastName ? participant.lastName : ""
                  }`
                : "Unnamed Participant"}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Chip
              label={participant.email}
              size="small"
              sx={{
                fontSize: "0.75rem",
                height: "24px",
                bgcolor: "background.default",
                color: "text.secondary",
                fontWeight: 500,
                borderRadius: "6px",
              }}
            />
            <IconButton
              size="small"
              onClick={handleViewClick}
              sx={{
                color: "primary.main",
                "&:hover": {
                  bgcolor: "rgba(135, 46, 229, 0.08)",
                },
              }}
              data-testid={`button-view-${participant.id}`}
            >
              <Visibility sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            color: "text.secondary",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: "regular" }}>
            {participant.gender}
          </Typography>
          {participant.age && (
            <>
              <Typography variant="body2">•</Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "regular",
                }}
              >
                {participant.age}
              </Typography>
            </>
          )}
          {participant.phoneNumber && (
            <>
              <Typography variant="body2">•</Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "regular",
                }}
              >
                {participant.phoneNumber}
              </Typography>
            </>
          )}
        </Box>
      </Box>
    </Card>
  );
};

export const VirtualizedParticipantList = ({
  participants,
  selectedParticipants,
  handleCheckboxToggle,
  onParticipantClick,
}: {
  participants: CohortParticipant[];
  selectedParticipants: Set<string>;
  handleCheckboxToggle: (
    participantId: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onParticipantClick: (participantId: string) => void;
}) => {
  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: participants.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => PREVIEW_ROW_ESTIMATE_PX,
    overscan: 5,
  });

  const items = virtualizer.getVirtualItems();

  return (
    <Box
      ref={parentRef}
      sx={{
        maxHeight: "500px",
        overflowY: "auto",
        px: 3,
        pb: 1,
      }}
    >
      <div
        style={{
          height: virtualizer.getTotalSize(),
          width: "100%",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            transform: `translateY(${items[0]?.start ?? 0}px)`,
          }}
        >
          {items.map((virtualRow) => {
            const participant = participants[virtualRow.index];
            return (
              <div
                key={virtualRow.key}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                style={{ paddingBottom: 16 }}
              >
                <ParticipantCard
                  participant={participant}
                  selectedParticipants={selectedParticipants}
                  handleCheckboxToggle={handleCheckboxToggle}
                  onParticipantClick={onParticipantClick}
                />
              </div>
            );
          })}
        </div>
      </div>
    </Box>
  );
};
